import { ParsedLocation, redirect } from '@tanstack/react-router';
import { User } from 'oidc-client-ts';
import { useAuth } from 'react-oidc-context';

import { env } from '@/app/conf/env';

/**
 * Ensures the user is authenticated before proceeding.
 * If the user is not authenticated, redirects them to the login page.
 *
 * @param param - The parameters object.
 * @param param.auth - The authentication object, typically obtained from the useAuth hook.
 * @param param.location - The current location object containing the URL details.
 * @return Throws a redirect if the user is not authenticated.
 */
export function requireAuth({ auth, location }: { auth: ReturnType<typeof useAuth>; location: ParsedLocation }) {
  if (!auth.isAuthenticated) {
    throw redirect({
      to: '/auth/login',
      search: { redirectTo: location.href }
    });
  }
}

/**
 * Retrieves the OIDC user information from either localStorage or sessionStorage.
 *
 * The method checks localStorage first using a constructed storage key based on
 * OIDC authority and client ID. If not found in localStorage, it falls back to sessionStorage.
 * Returns an instance of the User object if the information is available; otherwise, returns null.
 *
 * @return An instance of the User object if the user information is found; otherwise null.
 */
export function getUser() {
  const storageKey = `oidc.user:${env.OIDC_AUTHORITY}:${env.OIDC_CLIENT_ID}`;
  let oidcUser = localStorage.getItem(storageKey);
  if (!oidcUser) {
    oidcUser = sessionStorage.getItem(storageKey);
  }

  return oidcUser ? User.fromStorageString(oidcUser) : null;
}
